import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as General from "common/general";
import * as Constants from "common/constants";
import { getAllDatabaseRecords } from "components/workflow/WorkflowHelper";
import Select from "react-select";
import { startWorkflowProcess } from "components/workflow/StartWorkflowProcess";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const getCurrentDateTimeLocal = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};


const INITIAL_FILTERS = {
  FromTimeStamp: "2026-01-01T00:00",
  ToTimeStamp: getCurrentDateTimeLocal(),
  ViolationNumber: "",
  FineGroupCode: "",
  DisputeStatus: 1,
  FineBusinessReference: "",
  ResolutionDate: "",
  ResolutionSourceCode: "",
  CityHappenedCode: "",
};

const formatDateTimeForApi = (value) => {
  if (!value) {
    return null;
  }
  return `${value}:00`;
};

/**
 * Safely display a value.
 */
const displayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const ManageDispute = ({ }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.toLowerCase().startsWith("ar");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [disputes, setDisputes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsByPage, setRecordsByPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [finGroup, setFineGroup] = useState([]);
  const navigate = useNavigate();
  const [workflowPD, setWorkflowPD] = useState([]);
  const [searchedDisputeStatus, setSearchedDisputeStatus] = useState(INITIAL_FILTERS.DisputeStatus);
  /*
  |--------------------------------------------------------------------------
  | Get all Cities
  |--------------------------------------------------------------------------
  */
  const getTableData = async (tableName, fieldValue) => {
    try {
      const result = await getAllDatabaseRecords(tableName, fieldValue);
      setCities(result.data.dataBaseContent);
      return result;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const getTableDatafinegroup = async (tableName, fieldValue) => {
    try {
      const result = await getAllDatabaseRecords(tableName, fieldValue);
      setFineGroup(result.data.dataBaseContent);
      return result;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };
  const getWorkflowPD = async (tableName, fieldValue) => {
    try {
      const result = await getAllDatabaseRecords(tableName, fieldValue);
      setWorkflowPD(result.data.dataBaseContent[0].latest_PD);
      return result;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Filter Change
  |--------------------------------------------------------------------------
  */

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | Build Request Payload
  |--------------------------------------------------------------------------
  */

  const buildRequestPayload = useCallback(() => {
    const payload = {
      // Required fields
      FromTimeStamp: formatDateTimeForApi(filters.FromTimeStamp),
      ToTimeStamp: formatDateTimeForApi(filters.ToTimeStamp),

      OperatorID: 1000011021,
      ClientIPAddress: "192.168.1.1",

      PageRow: recordsByPage,
      PageNumber: currentPage,
    };

    // Optional filters
    if (filters.ViolationNumber?.trim()) {
      payload.ViolationNumber = filters.ViolationNumber.trim();
    }

    if (filters.FineGroupCode !== "" && filters.FineGroupCode != null) {
      payload.FineGroupCode = Number(filters.FineGroupCode);
    }

    if (filters.DisputeStatus !== "" && filters.DisputeStatus != null) {
      payload.DisputeStatus = Number(filters.DisputeStatus);
    }

    if (filters.FineBusinessReference?.trim()) {
      payload.FineBusinessReference =
        filters.FineBusinessReference.trim();
    }

    if (filters.ResolutionDate) {
      payload.ResolutionDate = filters.ResolutionDate;
    }

    if (
      filters.ResolutionSourceCode !== "" &&
      filters.ResolutionSourceCode != null
    ) {
      payload.ResolutionSourceCode =
        Number(filters.ResolutionSourceCode);
    }

    if (
      filters.CityHappenedCode !== "" &&
      filters.CityHappenedCode != null
    ) {
      payload.CityHappenedCode =
        Number(filters.CityHappenedCode);
    }

    return payload;
  }, [filters, currentPage, recordsByPage]);
  /*
  |--------------------------------------------------------------------------
  | Fetch Disputes
  |--------------------------------------------------------------------------
  */


  const fetchDisputes = useCallback(() => {
    setLoading(true);
    setError("");

    const payload = buildRequestPayload();

    const onSuccess = (responseObject) => {
      try {
        let list = [];

        try {
          if (Array.isArray(responseObject?.listOfDisputes)) {
            list = responseObject.listOfDisputes;
          } else if (typeof responseObject?.listOfDisputes === "string") {
            list = JSON.parse(responseObject.listOfDisputes);
          }
        } catch (error) {
          console.error("Failed to parse listOfDisputes:", error);
          list = [];
        }

        // console.log("Dispute list:", list);

        setDisputes(list);

        setTotalRecords(Number(responseObject?.totalRecords) || list.length);
      } catch (err) {
        setDisputes([]);
        setTotalRecords(0);

        setError(err?.message || t("failedToLoadDisputes"));
      } finally {
        toast.success(responseObject.httpResponse.message, {
          position: "top-right",
          autoClose: 3000,
        });
        setLoading(false);
      }
    };

    const onFailure = (error) => {
      toast.error(error,{
        position: "top-right",
        autoClose: 3000,
      });
     
      setDisputes([]);
      setTotalRecords(0);

      setError(error?.response?.data?.message || error?.message || t("failedToLoadDisputes"));

      setLoading(false);
    };

    General.PostToEndPoint(Constants.EfaaGetDisputeListEndPoint, null, payload, onSuccess, onFailure);

  }, [buildRequestPayload, currentPage, recordsByPage, t,]);


  /*
  |--------------------------------------------------------------------------
  | Search
  |--------------------------------------------------------------------------
  */

  const handleSearch = () => {
    setSearchedDisputeStatus(filters.DisputeStatus);
    setCurrentPage(1);
    fetchDisputes();
  };

  useEffect(() => {
    // Fetch cities on component mount
    getWorkflowPD("tbl_workflows", "workflowId=eq.tbl_efaa_objection");
    getTableData("facility_cities");
    getTableDatafinegroup("tbl_fine_group");
    fetchDisputes();
  }, []);
  /*
  |--------------------------------------------------------------------------
  | Clear
  |--------------------------------------------------------------------------
  */

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);

    setCurrentPage(1);
  };

  /*
  |--------------------------------------------------------------------------
  | Page change
  |--------------------------------------------------------------------------
  */

  const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / recordsByPage) : 1;

  const handlePreviousPage = () => {
    setCurrentPage((previous) =>
      Math.max(previous - 1, 1)
    );
  };

  const handleNextPage = () => {
    setCurrentPage((previous) =>
      Math.min(previous + 1, totalPages)
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Review
  |--------------------------------------------------------------------------
  */

  const handleReview = async (dispute) => {
    const initialValue = {
      disputeNumber: dispute.disputeNumber,
      violationNumber: dispute.violationNumber,
      fineGroupCode: dispute.fineGroupCode,
      fineGroupDescAr: dispute.fineGroupDescAr,
      mobileNumber: dispute.mobileNumber,
      disputeStatus: dispute.disputeStatus,
      disputeStatusDescAr: dispute.disputeStatusDescAr,
      cityHappenedCode: dispute.cityHappenedCode,
      cityHappenedDescAr: dispute.cityHappenedDescAr,
      violatorID: dispute.violatorID,
    };

    try {
      var formData = await startWorkflowProcess(workflowPD, true, initialValue);
      formData.settings.data = initialValue
      navigate("/form_view_renderer", {
        state: {
          data: formData,
        },
      });
    } catch (error) {
      console.error("Error starting workflow:", error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Localization Helpers
  |--------------------------------------------------------------------------
  */

  const getFineGroupName = (dispute) => {
    if (isArabic) {
      return (
        dispute?.fineGroupDescAr ||
        dispute?.fineGroupDescEn ||
        "-"
      );
    }

    return (
      dispute?.fineGroupDescEn ||
      dispute?.fineGroupDescAr ||
      "-"
    );
  };

  const getStatus = (dispute) => {
    if (isArabic) {
      return (
        dispute?.disputeStatusDescAr ||
        dispute?.disputeStatusDescEn ||
        "-"
      );
    }

    return (
      dispute?.disputeStatusDescEn ||
      dispute?.disputeStatusDescAr ||
      "-"
    );
  };

  const getCity = (dispute) => {
    if (isArabic) {
      return (
        dispute?.cityHappenedDescAr ||
        dispute?.cityHappenedDescEn ||
        "-"
      );
    }

    return (
      dispute?.cityHappenedDescEn ||
      dispute?.cityHappenedDescAr ||
      "-"
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div
      className="container-fluid py-3"
      dir={isArabic ? "rtl" : "ltr"}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                           */}
      {/* ---------------------------------------------------------------- */}

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-4">
        <div>
          <h3 className="mb-1 fw-bold">
            {t("manageDisputes")}
          </h3>

          <div className="text-muted">
            {t("manageDisputesDescription")}
          </div>
        </div>

        <Button
          variant="outline-secondary"
          onClick={fetchDisputes}
          disabled={loading}
        >
          {loading ? (
            <Spinner
              animation="border"
              size="sm"
              className="me-1"
            />
          ) : (
            "↻"
          )}

          {t("refresh")}
        </Button>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Error                                                            */}
      {/* ---------------------------------------------------------------- */}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Filters                                                          */}
      {/* ---------------------------------------------------------------- */}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">
              {t("searchFilters")}
            </h5>
          </div>

          <hr />

          <Row className="g-3">
            {/* From */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>
                  {t("fromTimeStamp")}
                </Form.Label>

                <Form.Control
                  type="datetime-local"
                  value={filters.FromTimeStamp}
                  onChange={(e) =>
                    handleFilterChange("FromTimeStamp", e.target.value)
                  }
                />
              </Form.Group>
            </Col>

            {/* To */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>
                  {t("toTimeStamp")}
                </Form.Label>

                <Form.Control
                  type="datetime-local"
                  value={filters.ToTimeStamp}
                  onChange={(e) =>
                    handleFilterChange("ToTimeStamp", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            {/* Dispute Status */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>{t("disputeStatus")}</Form.Label>
                <Form.Select
                  value={filters.DisputeStatus}
                  onChange={(e) =>
                    handleFilterChange("DisputeStatus", e.target.value)
                  }>
                  <option value="1">تحت المعالجة</option>
                  <option value="2">تم قبول الاعتراض للالغاء</option>
                  <option value="3">تم قبول الاعتراض للتحديث</option>
                  <option value="4">مرفوض</option>

                </Form.Select>
              </Form.Group>
            </Col>

            {/* Fine Group */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>{t("fineGroupCode")}</Form.Label>

                <Select
                  value={
                    finGroup
                      .map((group) => ({
                        value: group.fineGroupCode,
                        label: group.fineGroupDescAr,
                      }))
                      .find(
                        (option) =>
                          option.value === filters.FineGroupCode
                      ) || null
                  }
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "FineGroupCode",
                      selectedOption?.value || ""
                    )
                  }
                  options={finGroup.map((group) => ({
                    value: group.fineGroupCode,
                    label: group.fineGroupDescAr,
                  }))}
                  isClearable
                  isSearchable
                  placeholder={t("selectFineGroup")}
                  noOptionsMessage={() => t("noResults")}
                />
              </Form.Group>
            </Col>
            {/* City */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>{t("cityHappenedCode")}</Form.Label>

                <Select
                  value={
                    cities
                      .map((city) => ({
                        value: city.areaCode,
                        label: city.cityName,
                      }))
                      .find(
                        (option) =>
                          option.value === filters.CityHappenedCode
                      ) || null
                  }
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "CityHappenedCode",
                      selectedOption?.value || ""
                    )
                  }
                  options={cities.map((city) => ({
                    value: city.areaCode,
                    label: city.cityName,
                  }))}
                  isClearable
                  isSearchable
                  placeholder={t("selectCity")}
                  noOptionsMessage={() => t("noResults")}
                />
              </Form.Group>
            </Col>
            {/* Violation */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>
                  {t("violationNumber")}
                </Form.Label>

                <Form.Control
                  type="text"
                  value={filters.ViolationNumber}
                  onChange={(e) =>
                    handleFilterChange("ViolationNumber", e.target.value)
                  }
                />
              </Form.Group>
            </Col>

            {/* Resolution Date */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>{t("resolutionDate")}</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.ResolutionDate}
                  onChange={(e) =>
                    handleFilterChange("ResolutionDate", e.target.value)
                  }
                />
              </Form.Group>
            </Col>

            {/* Resolution Source */}
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>{t("resolutionSourceCode")}</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={
                    filters.ResolutionSourceCode
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "ResolutionSourceCode",
                      e.target.value.replace(/\D/g, "")
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Buttons */}
          <div
            className={`d-flex gap-2 mt-4 ${isArabic
              ? "justify-content-start"
              : "justify-content-end"
              }`}
          >
            <Button
              variant="outline-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              {t("clear")}
            </Button>

            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              ) : (
                ""
              )}
              {t("search")}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Table                                                            */}
      {/* ---------------------------------------------------------------- */}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {/* Table Header */}
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h5 className="mb-0 fw-bold">
              {t("title")}
            </h5>

            <span className="text-muted">
              {t("total")}: {totalRecords}
            </span>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <Table
              hover
              bordered
              className="mb-0 align-middle"
            >
              <thead className="table-light">
                <tr>
                  <th className="text-nowrap">
                    {t("violatorID")}
                  </th>
                  <th className="text-nowrap">
                    {t("disputeNumber")}
                  </th>

                  <th className="text-nowrap">
                    {t("violationNumber")}
                  </th>

                  <th>
                    {t("fineGroup")}
                  </th>

                  <th className="text-nowrap">
                    {t("mobileNumber")}
                  </th>

                  <th>
                    {t("status")}
                  </th>

                  <th>
                    {t("city")}
                  </th>

                  <th
                    className="text-center text-nowrap"
                    style={{
                      width: "100px",
                    }}
                  >
                    {t("actions")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* Loading */}
                {loading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-5"
                    >
                      <Spinner
                        animation="border"
                        role="status"
                      />

                      <div className="text-muted mt-2">
                        {t("loading")}
                      </div>
                    </td>
                  </tr>
                )}

                {/* Empty */}
                {!loading &&
                  disputes.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center text-muted py-5"
                      >
                        {t("noData")}
                      </td>
                    </tr>
                  )}

                {/* Data */}
                {!loading &&
                  disputes.map((dispute, index) => (
                    <tr
                      key={
                        dispute.disputeNumber ??
                        `${dispute.violationNumber}-${index}`
                      }
                    >

                      <td>
                        {displayValue(
                          dispute.violatorID
                        )}
                      </td>
                      <td>
                        {displayValue(
                          dispute.disputeNumber
                        )}
                      </td>

                      <td>
                        {displayValue(
                          dispute.violationNumber
                        )}
                      </td>

                      <td
                        style={{
                          minWidth: "250px",
                        }}
                      >
                        {getFineGroupName(
                          dispute
                        )}
                      </td>

                      <td>
                        {displayValue(
                          dispute.mobileNumber
                        )}
                      </td>

                      <td>
                        {getStatus(dispute)}
                      </td>

                      <td>
                        {getCity(dispute)}
                      </td>

                      <td className="text-center">
                        {Number(searchedDisputeStatus) === 1 && <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleReview(dispute)
                          }
                        >
                          {t("review")}
                        </Button>
                        }
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>

          {/* -------------------------------------------------------------- */}
          {/* Pagination                                                       */}
          {/* -------------------------------------------------------------- */}

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 gap-3 border-top">
            {/* Records per page */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted">
                {t("rowsPerPage")}
              </span>

              <Form.Select
                size="sm"
                value={recordsByPage}
                onChange={(e) => {
                  setRecordsByPage(
                    Number(e.target.value)
                  );
                  setCurrentPage(1);
                }}
                style={{
                  width: "80px",
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            {/* Pagination */}
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={
                  loading ||
                  currentPage <= 1
                }
                onClick={handlePreviousPage}
              >
                {t("previous")}
              </Button>

              <span className="px-2">
                {t("page") + " " + currentPage + " " + t("of") + " " + totalPages}
              </span>

              <Button
                variant="outline-secondary"
                size="sm"
                disabled={
                  loading ||
                  currentPage >= totalPages
                }
                onClick={handleNextPage}
              >
                {t("next")}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ManageDispute;